import type { AttendingTaskRow } from '../data/attendingTasks';
import { uniqueColumnSuggestions } from '../utils/columnFilter';
import { caseApiRequest } from './apiClient';
import { adaptAttendingTaskRow } from './attendingTaskAdapter';
import { type ApiTaskRow } from './taskListAdapter';
import { resolveTaskListTotal } from './tasks';

export type AttendingTaskListResult = {
  items: AttendingTaskRow[];
  total: number;
  totalPages: number;
};

export type AttendingTaskListParams = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  columnFilters?: Record<string, string>;
  signal?: AbortSignal;
};

type ApiTaskPage = {
  content?: (ApiTaskRow & Record<string, unknown>)[];
  totalElements?: number;
  totalPages?: number;
};

/** DataTable column key → POST `/state-attorney/task-details` filter field (snake_case). */
export const ATTENDING_FILTER_COL_MAP: Record<string, string> = {
  ref: 'file_ref_no',
  fileCategory: 'file_category',
  subFileCategory: 'sub_file_category',
  agency: 'submitted_agency',
  caseNumber: 'case_number',
  stage: 'current_workflow_stage',
  status: 'task_status',
  urgency: 'urgency_level',
  officer: 'assigned_officer',
  supervisor: 'supervisor',
};

/** DataTable column key → `sort_by` value (snake_case). */
export const ATTENDING_SORT_COL_MAP: Record<string, string> = {
  ref: 'file_ref_no',
  fileCategory: 'file_category',
  subFileCategory: 'sub_file_category',
  agency: 'submitted_agency',
  submittedAt: 'submitted_at',
  caseNumber: 'case_number',
  stage: 'current_workflow_stage',
  status: 'task_status',
  time: 'time_remaining_display',
  urgency: 'urgency_level',
  officer: 'assigned_officer',
  supervisor: 'supervisor',
};

/** POST `/v1/case-requests/state-attorney/task-details` — State Attorney attending tasks list. */
const LIST_ENDPOINT = '/state-attorney/task-details';

function submittedAtRangeFilterToIso(fromRaw: string, toRaw: string): { from?: string; to?: string } {
  const from = fromRaw.trim().slice(0, 10);
  const to = toRaw.trim().slice(0, 10);
  const out: { from?: string; to?: string } = {};
  if (/^\d{4}-\d{2}-\d{2}$/.test(from)) out.from = `${from}T00:00:00.000Z`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(to)) out.to = `${to}T23:59:59.999Z`;
  return out;
}

export function buildAttendingListBody(params: AttendingTaskListParams): Record<string, string> {
  const body: Record<string, string> = {
    sort_by: ATTENDING_SORT_COL_MAP[params.sortBy ?? 'ref'] ?? 'file_ref_no',
    sort_direction: params.sortDir ?? 'ASC',
  };

  if (params.columnFilters) {
    for (const [colId, value] of Object.entries(params.columnFilters)) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      if (colId === 'submittedAt') {
        const pipe = trimmed.indexOf('|');
        const from = pipe >= 0 ? trimmed.slice(0, pipe).trim() : trimmed;
        const to = pipe >= 0 ? trimmed.slice(pipe + 1).trim() : from;
        const range = submittedAtRangeFilterToIso(from, to);
        if (range.from) body.submitted_at_from = range.from;
        if (range.to) body.submitted_at_to = range.to;
        continue;
      }
      const apiField = ATTENDING_FILTER_COL_MAP[colId];
      if (apiField) body[apiField] = trimmed;
    }
  }

  return body;
}

export async function listAttendingTasks(params: AttendingTaskListParams = {}): Promise<AttendingTaskListResult> {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 5;
  const qs = new URLSearchParams({
    pageNumber: String(page),
    pageSize: String(pageSize),
  });

  const res = await caseApiRequest<ApiTaskPage>(`${LIST_ENDPOINT}?${qs.toString()}`, {
    method: 'POST',
    body: buildAttendingListBody(params),
    signal: params.signal,
  });

  if (!res.ok) {
    throw new Error(res.message);
  }

  const content = res.data.content ?? [];
  const total = resolveTaskListTotal(res.data.totalElements, content.length);
  const totalPages =
    typeof res.data.totalPages === 'number' && res.data.totalPages > 0
      ? res.data.totalPages
      : total === 0
        ? 1
        : Math.max(1, Math.ceil(total / pageSize));

  return {
    items: content.map((r) => adaptAttendingTaskRow(r)),
    total,
    totalPages,
  };
}

export async function fetchAttendingColumnSuggestions(
  columnKey: string,
  prefix: string,
): Promise<string[]> {
  const col = ATTENDING_FILTER_COL_MAP[columnKey] ? columnKey : 'ref';
  const result = await listAttendingTasks({ page: 0, pageSize: 200, columnFilters: prefix ? { [col]: prefix } : {} });
  const getter = (r: AttendingTaskRow): string => {
    switch (col) {
      case 'fileCategory':
        return r.fileCategory;
      case 'subFileCategory':
        return r.subFileCategory;
      case 'agency':
        return r.agency;
      case 'caseNumber':
        return r.caseNumber;
      case 'stage':
        return r.stage;
      case 'status':
        return r.status;
      case 'urgency':
        return r.urgencyLevel;
      case 'officer':
        return r.assignedOfficer;
      case 'supervisor':
        return r.supervisor;
      default:
        return r.ref;
    }
  };
  return uniqueColumnSuggestions(result.items.map(getter), prefix);
}
